<template>
    <div class="meeting-wrapper">
        <div :class="['attendant-video', callStatus]" :style="attendantCss">
            <div class="permission-error" v-if="permissionError">
                <PermissionGrey></PermissionGrey>
            </div>
            <template v-else>
                <div v-show="!askingPermissions" :class="['video-wrapper', { overlay: (muted && !paused)}]">
                    <video ref="videoElement" class="self-video"></video>
                    <template v-if="callStatus === 'start' || callStatus === 'started'">
                        <div class="status audio-status" v-if="muted && !paused">
                            <AudioMuted></AudioMuted>
                        </div>
                        <div class="status video-status" v-else-if="paused && !muted">
                            <VideoPaused></VideoPaused>
                        </div>
                        <div class="status audio-video-status" v-else-if="muted && paused">
                            <AudioMuted></AudioMuted>
                            <VideoPaused></VideoPaused>
                        </div>
                    </template>
                </div>
            </template>
            
        </div>
        <MeetingStatus 
            :patient-name="patientName"
            :permission-error="permissionError"
            :asking-permissions="askingPermissions"
            :api-failed="callStatus === 'failed'"></MeetingStatus>
        <audio ref="audioElement" ></audio>
        <div class="attendee-video" v-show="callStatus === 'start' || callStatus === 'started'">
                <MeetingControls
                    :call-status="callStatus"
                    :paused="paused"
                    @toggle-video="toggleVideo"
                    :muted="muted"
                    @toggle-audio="toggleAudio"
                    :attendee-paused="attendeePaused"
                    @rotate-video="rotateVideo"
                    @end-meeting="endMeeting"
                ></MeetingControls>
                <div class="attendee-status" v-show="callStatus === 'start' && !unresponsiveCall">
                    <LoadSpinner></LoadSpinner>
                    <span>
                        Waiting for {{ patientName }}
                    </span>
                    <span>
                        to answer the call
                    </span>
                </div>
                <div class="attendee-status" v-show="callStatus === 'start' && unresponsiveCall">
                    {{ patientName }} did not pickup the call
                </div>
                <div class="attendee-status" v-show="callStatus === 'started' && !attendeePresent">
                    {{ patientName }} has left the meeting
                </div>
                
                <div class="video-wrapper"
                    v-show="callStatus === 'started' && attendeePresent" 
                    :style="{width : attendeeWrapperWidth + 'px'}"
                    :class="{ overlay : (attendeeMuted && !attendeePaused)}" >
                    <video ref="attendeeVideoElement" :class="['other-video', { rotate: rotateOn }]" ></video>
                    <div class="status audio-status" v-if="attendeeMuted && !attendeePaused">
                        <AudioMuted></AudioMuted>
                        <i>
                            Audio Paused
                        </i>
                    </div>
                    <div class="status video-status" v-else-if="attendeePaused && !attendeeMuted">
                        <VideoPaused></VideoPaused>
                        <i>Video Paused</i>
                    </div>
                    <div class="status audio-video-status" v-else-if="attendeeMuted && attendeePaused">
                        <span>
                            <AudioMuted></AudioMuted>
                            &nbsp;&nbsp;
                            <VideoPaused></VideoPaused>
                        </span>
                        <i>
                            Video and Audio Paused
                        </i>
                    </div>
                </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import {
    ConsoleLogger,
    DefaultDeviceController,
    DefaultMeetingSession,
    LogLevel,
    MeetingSessionConfiguration,
    DefaultModality
} from 'amazon-chime-sdk-js'

import MeetingStatus from './MeetingStatus'
import MeetingControls from './MeetingControls'
import MeetingIcons from './icon'
import LoadSpinner from '../component/icon/LoadSpinner'


/**
 * Main component for creating meeting instance, getting permissions and
 * conducting the meeting
 */
export default {
    name: 'Meeting',

    props: {
        /**
         * Id of the patient for the call
         */
        patientId: {
            type: String,
            required: true
        }
    },

    data() {
        const logger = new ConsoleLogger('MyLogger', LogLevel.WARN)
        return {
            /**
             * Log generator for aws chime
             */
            logger,
            deviceController: new DefaultDeviceController(logger),
            meetingSession: null,
            /**
             * List of audio input devices 
             * (ex Microphone)
             */
            audioInputDevices: null,
            /**
             * List of audio output devices
             * (ex speakers, headphones)
             */
            audioOutputDevices: null,
            /**
             * List of video input devices
             * (ex camera)
             */
            videoInputDevices: null,
            /**
             * Dimensions for the attendant video wrapper,
             * set once the video starts
             */
            attendantDimensions: {},
            /**
             * Doctor has paused video
             */
            paused: false,
            /**
             * Doctor has muted audio
             */
            muted: false,
            /**
             * Patient has paused video
             */
            attendeePaused: true,
            /**
             * Patient has muted audio 
             */
            attendeeMuted: false,
            /**
             * Local chime tile id for Doctor video
             */
            localTileId: null,
            /**
             * Rotate the patient video by 90 deg
             */
            rotateOn: false,
            /**
             * Status if permission for microphone and camera are
             * being asked
             */
            askingPermissions: false,
            /**
             * Status if asking for permission for camera and
             * microphone has ended
             */
            askingPermissionsEnded: false,
            /**
             * `true` if browser permissions
             * get blocked
             */
            permissionError: false,
            /**
             * Status of the current call
             * `ready` Indicates that the call is ready to get be placed.
             * `start` Indicates that we have the video for the doctor.
             * `started` Indicates that we have the patients video.
             */
            callStatus: 'ready',
            /**
             * Status whether call was not 
             * responed to
             */
            unresponsiveCall: false,
            /**
             * Name of the patient for the call
             */
            patientName: this.$route.query.name ? this.$route.query.name : 'Patient',
            /**
             * Provider id of the account calling to patient
             */
            patientProvider: this.$route.query.provider ? this.$route.query.provider : null,
            /**
             * Status whether attendee is 
             * present or not
             */
            attendeePresent: false,
            /**
             * Present width of the wrapper 
             * surrounding patient video
             */
            attendeeWrapperWidth: 700
        }
    },

    async mounted() {
        if(this.patientProvider)
            this.setCurrentProvider(this.patientProvider)
        await this.getMeetingDetails()
        if(this.callStatus !== 'failed') {
            await this.getMediaDevices()
            if(!this.permissionError) {
                await this.addMediaDevices()
                await this.startSession()
            }
        }
        
        
    },

    methods: {
        ...mapActions('user', ['createMeeting']),
        ...mapActions('provider', ['setCurrentProvider']),
        /**
         * Creates a meeting for the patient through the api 
         * and get the meeting info (`meetingResponse` and `attendeeResponse`)
         */
        async getMeetingDetails() {
            try {
                //Creates a new meeting
                const MeetingObj = await this.createMeeting(this.patientId)
                this.setMeetingEndOnNoResponse()
                // Get the meeting and attendee responses and set configuration with them
                const meetingResponse = MeetingObj.JoinInfo.Meeting
                const attendeeResponse = MeetingObj.JoinInfo.Attendee
                const configuration = new MeetingSessionConfiguration(meetingResponse, attendeeResponse)
                // Set the meeting session with the configuration and device controller
                this.meetingSession = new DefaultMeetingSession(configuration, this.logger, this.deviceController)
            } catch (error) {
                console.error('Failed in creating meeting', error)
                this.callStatus = 'failed'
            }
        },
        /**
         * Get permission for media devices (if not given) and
         * getting list of audio input, audio output and video input devices
         */
        async getMediaDevices() {
            /**
             * Override behavious of default media devices get by adding
             * `this.askingPermissions`, `this.askingPermissionsEnded` and
             * `this.permissionError` for showing custom UX for different results
             * produced by permissions
             */
            this.meetingSession.audioVideo.setDeviceLabelTrigger(async () => {
                setTimeout(() => {
                    if(!this.askingPermissionsEnded)
                        this.askingPermissions = true
                }, 200)
                let stream
                try {
                    stream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                        video: true
                    })
                } catch (error) {
                    console.error('Problem getting permissions ', error)
                    this.permissionError = true
                }
                this.askingPermissionsEnded = true
                this.askingPermissions = false
                return stream
            })
            this.audioInputDevices = await this.meetingSession.audioVideo.listAudioInputDevices()
            this.audioOutputDevices = await this.meetingSession.audioVideo.listAudioOutputDevices()
            this.videoInputDevices = await this.meetingSession.audioVideo.listVideoInputDevices()
        },
        /**
         * Setting media devices ID for the `meetingSession` from
         *  the first device got in the list
         */
        async addMediaDevices() {
            await this.meetingSession.audioVideo.chooseAudioInputDevice(this.audioInputDevices[0].deviceId)
            if(this.audioOutputDevices.length)
                await this.meetingSession.audioVideo.chooseAudioOutputDevice(this.audioOutputDevices[0].deviceId)
        },
        /**
         * Starts the session binding audio video refs to 
         * `meetingSession`
         */
        async startSession() {
            this.meetingSession.audioVideo.bindAudioElement(this.$refs.audioElement)
            const observer = {
                audioVideoDidStart: () => {
                }
            }

            this.meetingSession.audioVideo.addObserver(observer)
            this.meetingSession.audioVideo.start()

            await this.addLocalVideo()

            let localTileId = null
            const attendeeObserver = {
                 videoTileDidUpdate: tileState => {
                    // Ignore a tile without attendee ID, a local tile (your video), and a content share.
                    if (!tileState.boundAttendeeId || tileState.localTile || tileState.isContent) {
                        return
                    }
                    this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.$refs.attendeeVideoElement)
                    this.$refs.attendeeVideoElement.addEventListener('resize', this.onResize)
                    if(tileState.active) {
                        this.callStatus = 'started'
                        this.attendeePaused = false
                    }
                },
                videoTileWasRemoved: tileId => {
                    if(tileId !== this.localTileId) {
                        this.attendeePaused = true
                        if(this.$refs.attendeeVideoElement)
                            this.$refs.attendeeVideoElement.removeEventListener('resize', this.onResize)
                    }
                }
            }

            const attendeePresenceSet = new Set()
            const callback = (presentAttendeeId, present) => {
                if (present) {
                    attendeePresenceSet.add(presentAttendeeId)
                    this.attendeePresent = true
                    this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
                        presentAttendeeId,
                        (attendeeId, volume, muted, signalStrength) => {
                            const baseAttendeeId = new DefaultModality(attendeeId).base()
                            const myId = this.meetingSession.configuration.credentials.attendeeId
                            if (baseAttendeeId !== attendeeId || attendeeId === myId)
                                return
                            this.callStatus = 'started'
                            if(muted === true || muted === false)
                                this.attendeeMuted = muted
                        }
                    )
                } else {
                    attendeePresenceSet.delete(presentAttendeeId)
                    this.attendeePresent = false
                }
            }

            this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(callback)

            this.meetingSession.audioVideo.addObserver(attendeeObserver)

        },
        /**
         * Chooses Video input devices and add Observer for 
         * behaviour on video changes like pausing, etc for doctors video
         */
        async addLocalVideo() {
            await this.meetingSession.audioVideo.chooseVideoInputDevice(this.videoInputDevices[0].deviceId)
            const videoObserver = {
                // videoTileDidUpdate is called whenever a new tile is created or tileState changes.
                videoTileDidUpdate: tileState => {
                    // Ignore a tile without attendee ID and other attendee's tile.
                    if (!tileState.boundAttendeeId || !tileState.localTile) {
                        return
                    }
                    this.setAttendantDimensions(tileState)
                    // Binding video stream to video element
                    this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.$refs.videoElement)

                    if(tileState.active && this.callStatus !== 'started') {
                        this.callStatus = 'start'
                    }
                    this.localTileId = tileState.tileId
                    if(this.callStatus == 'start' || this.callStatus == 'started')
                        this.paused = !tileState.active
                }
            }

            this.meetingSession.audioVideo.addObserver(videoObserver)
            this.meetingSession.audioVideo.startLocalVideoTile()

        },
        /**
         * End the current meeting
         * by closing window
         */
        closeWindow() {
            window.close()
        },
        /**
         * Set meeting to end on non responsive
         * for 60 seconds
         */
        setMeetingEndOnNoResponse() {
            setTimeout(() => {
                if(this.callStatus === 'start') {
                    this.meetingSession.audioVideo.stop()
                    this.unresponsiveCall = true
                }
            }, 60000)
        },
        /**
         * End the meeting
         */
        endMeeting() {
            this.meetingSession.audioVideo.stop()
            this.closeWindow()
        },
        /**
         * Sets dimension for the attendant video wrapper
         *  to the dimensions of the video element
         */
        setAttendantDimensions(tileState) {
            if(tileState.active && tileState.videoStreamContentHeight && tileState.videoStreamContentWidth) {
                this.attendantDimensions = {
                    width: tileState.videoStreamContentWidth,
                    height:  tileState.videoStreamContentHeight
                }
            }
        },
        async toggleVideo() {
            if(!this.meetingSession)
                return
            if(this.paused) {
                await this.meetingSession.audioVideo.chooseVideoInputDevice(this.videoInputDevices[0].deviceId)
                this.meetingSession.audioVideo.startLocalVideoTile()
            }
            else {
                this.meetingSession.audioVideo.stopLocalVideoTile()
                this.meetingSession.audioVideo.removeLocalVideoTile()
            }
        },
        toggleAudio() {
            if(this.muted)
                this.meetingSession.audioVideo.realtimeUnmuteLocalAudio()
            else
                this.meetingSession.audioVideo.realtimeMuteLocalAudio()
            this.muted = this.meetingSession.audioVideo.realtimeIsLocalAudioMuted()
        },
        /**
         * Rotate current video of patient
         */
        rotateVideo() {
            this.rotateOn = !this.rotateOn
            this.$nextTick(() => this.onResize())
        },
        /**
         * Event used on resize of patient video
         */
        onResize() {
            const widthToSet = this.rotateOn ?  this.$refs.attendeeVideoElement.clientHeight : this.$refs.attendeeVideoElement.clientWidth
            this.attendeeWrapperWidth = widthToSet ? widthToSet : 700
        }
    },

    computed: {
        rotateStyle() {
            const rotateStyle = 'transform: rotate(90deg);height: 100vw;margin: 0;transform-origin: bottom left;width: 100vh;left: 0;bottom: 100%;'
            return this.rotateOn ? rotateStyle : 'transform: rotate(0deg)'
        },
        /**
         * Calculates css for the attendant video
         */
        attendantCss() {
            const maxWidth = this.callStatus !== 'ready' ? 215 : 700
            if(this.attendantDimensions && this.attendantDimensions.width && this.attendantDimensions.height) {
                const width = Math.min(this.attendantDimensions.width, maxWidth)
                return {
                    width: width + 'px',
                    height: (this.attendantDimensions.height * width / this.attendantDimensions.width) + 'px'
                }
            }
                return {}
        }
    },

    components: {
        MeetingStatus,
        MeetingControls,
        LoadSpinner,
        ...MeetingIcons
    }
}
</script>

<style lang="scss" scoped>
@import "style.scss"
</style>
